import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewEncapsulation, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { User } from '../../../hickory-api';
import { AnalyticsHelper } from '../../analytics/analytics-helper';
import { ApiSelectorComponent } from '../../api-selector/api-selector.component';
import { ApiSelectorService } from '../../api-selector/api-selector.service';
import { AppVersionService } from '../../app-version.service';
import { AuthService } from '../../authentication/auth.service';
import { FeaturesSupportedService } from '../../feature-filtering/features-supported-service';
import { ImpersonationComponent } from '../../impersonation/impersonation.component';
import { LogHelper } from '../../logging/log-helper';
import { ConfirmComponent } from '../../modals/confirm/confirm.component';
import {
  SelectFromListDialogComponent,
  SelectFromListResult,
} from '../../modals/select-from-list-dialog/select-from-list-dialog.component';
import { NotificationService } from '../../notifications/notification.service';
import { ChangePasswordDialogComponent } from '../../../user/change-password-dialog/change-password-dialog.component';
import { MfaService } from '../../../user/mfa.service';
import { ThemeService } from '../../theme/theme.service';
import { ThemeMode } from '../../user-preferences/user-preferences.service';
import { FeatureToggleService } from '../../feature-toggle.service';
import { FeaturesConfig } from '../../features-config';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

@Component({
  selector: 'app-account-menu',
  templateUrl: './account-menu.component.html',
  styleUrls: ['./account-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class AccountMenuComponent {
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiSelectorService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly featureService = inject(FeatureToggleService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly translation = inject(L10nTranslationService);
  protected readonly locale = inject<L10nLocale>(L10N_LOCALE);
  protected readonly notificationService = inject(NotificationService);
  protected readonly mfaService = inject(MfaService);
  protected readonly themeService = inject(ThemeService);

  setThemeMode(mode: ThemeMode): void {
    this.themeService.setThemeMode(mode);
  }

  private readonly log = new LogHelper('AccountMenu');
  readonly user = input<User | null>(null);
  readonly hasMfa = this.authService.hasMfa;

  readonly canChangeThemeMode = toSignal(
    this.featureService.updates.pipe(map(() => this.featureService.isEnabled(FeaturesConfig.THEME_DARK_MODE))),
    { initialValue: this.featureService.isEnabled(FeaturesConfig.THEME_DARK_MODE) },
  );

  get isImpersonating(): boolean {
    return this.authService.isImpersonating();
  }

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  get canImpersonate(): boolean {
    return this.isSuperAdmin;
  }

  get canChangePassword(): boolean {
    return !!this.user() && !this.isImpersonating && !this.isSSO;
  }

  get isSSO(): boolean { return this.authService.isSSO(); }

  /**
   * MFA setup is offered only when the tenant uses MFA *and* Balsa owns enrollment for this login —
   * SSO users get their MFA policy from the identity provider, so Alder's self-service page has
   * nothing to manage for them. Drives every MFA affordance in this menu, not just the button.
   */
  get canManageMfa(): boolean {
    return this.hasMfa() && this.mfaService.canManageMfa();
  }

  get hasSecuritySettings(): boolean {
    return this.canChangePassword || this.canManageMfa;
  }

  get securitySubtitle(): string {
    if (this.canChangePassword && this.canManageMfa) return 'MFA.SecuritySubtitlePasswordAndMfa';
    if (this.canManageMfa) return 'MFA.SecuritySubtitleMfaOnly';
    return 'MFA.SecuritySubtitlePasswordOnly';
  }

  get isChangeEnvironmentsEnabled(): boolean {
    return this.apiService.isEnabled;
  }

  get isChangedEnvironment(): boolean {
    return this.apiService.isActive;
  }

  get canOverrideFeaturesSupported(): boolean {
    return FeaturesSupportedService.canOverrideFeaturesSupported(this.authService);
  }

  get userDisplayName(): string | null {
    if (this.user()) {
      if (this.user()?.name?.givenName && this.user()?.name?.familyName) {
        return this.user()!.name!.givenName + ' ' + this.user()!.name!.familyName;
      } else {
        return this.translation.translate('Auth.SignedIn');
      }
    }
    return null;
  }

  get userEmail(): string {
    return this.user()?.identity?.username ?? this.user()?.contact?.emails?.[0]?.address ?? '';
  }

  get userTooltip(): string {
    let tip = this.userDisplayName;
    if (!tip) return '';
    if (this.user()?.identity?.username) tip += `\nUsername: ${this.user()!.identity!.username}`;
    if (this.user()?.agency && this.user()?.tenantId) {
      tip += `\nTenant: ${this.user()!.agency} (ID: ${this.user()!.tenantId})`;
    }
    return tip;
  }

  onUserClick(): void {
    this.log.debug('onUserClick');
    this.appVersionService.fetchLatestVersion().subscribe((latest) => {
      const current = this.appVersionService.detectAppVersion();
      console.log(`App Version: current=${current}; latest=${latest}`);
    });

    AnalyticsHelper.logEvent({
      action: 'user-button-click',
      name: 'ButtonClick',
    });
  }

  configureMfa(): void {
    if (!this.canManageMfa) return;

    this.mfaService.manageMfa();
  }

  changePassword(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '80vw',
      maxWidth: '800px',
      autoFocus: true,
      delayFocusTrap: true,
    });
  }

  showImpersonate(): void {
    this.dialog.open(ImpersonationComponent, { minWidth: '60vw', data: {} });
  }

  stopImpersonate(): void {
    this.authService.stopImpersonation();
  }

  showChangeApi(): void {
    this.dialog.open(ApiSelectorComponent, { width: '800px', data: {} });
  }

  showEnableChangeEnvironments(): void {
    this.dialog
      .open(ConfirmComponent, {
        width: '400px',
        data: {
          title: 'Enable Change Environment',
          message: 'Do you want to enable changing environments (for testing against alternate hostnames)?',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.apiService.enableChangeEnvironments();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  overrideFeatureSupported(): void {
    const dRef = this.dialog.open(SelectFromListDialogComponent, {
      ariaLabel: 'Override Supported Features',
      width: '600px',
      data: {
        title: 'Override Supported Features',
        items: FeaturesSupportedService.getFeatures().map((feature) => ({
          display: feature.feature,
          selected: feature.enabled,
          value: feature,
        })),
      },
      autoFocus: false,
    });

    dRef.afterClosed().subscribe((result: SelectFromListResult) => {
      if (result.result === 'select') {
        const features: string[] = result.selectedItems.map((o) => o.display);
        FeaturesSupportedService.overrideFeaturesSupported(this.authService, features);
        this.changeDetectorRef.markForCheck();
      }

      if (result.result === 'reset') {
        FeaturesSupportedService.resetFeaturesSupported(this.authService);
        this.changeDetectorRef.markForCheck();
      }

      if (result.result !== 'cancel') {
        const currentUrl = this.router.parseUrl(this.router.url);
        void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          console.log('Reloading page to apply feature changes');
          void this.router.navigate([currentUrl.toString().replace(/\?.*/, '')], {
            queryParams: currentUrl.queryParams,
            fragment: currentUrl.fragment!,
          });
        });
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }
}
